// คุยกับ RTDB stores/{storeId}/cashier_seats — ทุกฟังก์ชันรับ storeId ตรง ๆ
// (plugin รันก่อน bootstrap ตั้ง storeId ให้ useFirebase() จึงพึ่งตัวนั้นไม่ได้)
//
// กติกาการตัดสินทั้งหมดอยู่ที่ utils/seatRules.ts ไฟล์นี้แค่อ่าน / เขียนตามผลนั้น
//
// RTDB ออฟไลน์: get() / runTransaction() ไม่ reject แต่ค้างรอจนต่อติด
// ทุกฟังก์ชันจึงเช็ค .info/connected ก่อน และห่อ timeout ไว้เสมอ

import {
  get,
  getDatabase,
  onValue,
  ref,
  runTransaction,
  serverTimestamp,
  update,
  type DatabaseReference,
  type Unsubscribe,
} from "firebase/database";
import { useServerTime } from "../../core/utils/timestamp";
import {
  SEAT_MODE_MAIN,
  decideClaim,
  decideRelease,
  decideSwapSecond,
  toLocalIso,
  type ClaimDecision,
  type ClaimOptions,
  type SeatMode,
} from "../utils/seatRules";

/** รอ socket ต่อติดตอนเพิ่งโหลดหน้า */
export const CONNECT_WAIT_MS = 3000;
/** เน็ตร้านช้าได้ แต่ไม่ควรค้างนานกว่านี้ */
export const NETWORK_TIMEOUT_MS = 8000;

export function seatsRef(storeId: string): DatabaseReference {
  return ref(getDatabase(), `stores/${storeId}/cashier_seats`);
}

/** เวลา server (epoch ms) จาก .info/serverTimeOffset — กันนาฬิกาเครื่องเพี้ยน */
export function serverNow(): number {
  return useServerTime().getCorrectedDayjsInstance().valueOf();
}

export async function readSeats(storeId: string, timeoutMs = NETWORK_TIMEOUT_MS): Promise<any> {
  const snapshot = await withTimeout(get(seatsRef(storeId)), timeoutMs);
  return snapshot.val();
}

/**
 * จองที่นั่งด้วย transaction (กันสองเครื่องกดพร้อมกัน)
 * throw เมื่อคุยกับ server ไม่ได้ — ผู้เรียกตัดสินเองว่าจะปล่อยผ่านหรือไม่
 */
export async function claimSeat(
  storeId: string,
  opts: Omit<ClaimOptions, "now">
): Promise<ClaimDecision> {
  const ref = seatsRef(storeId);
  // อ่านก่อนหนึ่งรอบให้ cache อุ่น รอบแรกของ transaction จะได้เห็นค่าจริง ไม่ใช่ null
  await withTimeout(get(ref), NETWORK_TIMEOUT_MS);

  // callback ถูกเรียกซ้ำได้หลายรอบ ถือผลรอบล่าสุดไว้
  const box: { decision: ClaimDecision | null } = { decision: null };
  const result = await withTimeout(
    runTransaction(
      ref,
      (current) => {
        box.decision = decideClaim(current, { ...opts, now: serverNow() });
        // undefined = ยกเลิก (ไม่ว่าง / เต็ม) ไม่เขียนอะไร
        return box.decision.result === "ok" ? box.decision.next : undefined;
      },
      { applyLocally: false }
    ),
    NETWORK_TIMEOUT_MS
  );

  if (!box.decision) throw new Error("no decision");
  return result.committed ? { ...box.decision, result: "ok" } : box.decision;
}

/** แย่งที่นั่ง Second จาก kickId — ดู decideSwapSecond */
export async function swapSecond(
  storeId: string,
  opts: { me: string; name: string; kickId: string }
): Promise<"ok" | "not_found"> {
  const ref = seatsRef(storeId);
  await withTimeout(get(ref), NETWORK_TIMEOUT_MS);

  let outcome: "ok" | "not_found" = "not_found";
  await withTimeout(
    runTransaction(
      ref,
      (current) => {
        const decision = decideSwapSecond(current, { ...opts, now: serverNow() });
        outcome = decision.result;
        return decision.result === "ok" ? decision.next : undefined;
      },
      { applyLocally: false }
    ),
    NETWORK_TIMEOUT_MS
  );
  return outcome;
}

/** คืนที่นั่งทุกตำแหน่งของเครื่องนี้ — ไม่ throw (คืนไม่ได้ก็หลุดเองใน 5 นาที) */
export async function releaseSeats(storeId: string, me: string, timeoutMs = 3000): Promise<void> {
  try {
    await withTimeout(
      (async () => {
        if (!(await waitForConnected(Math.min(CONNECT_WAIT_MS, timeoutMs)))) return;
        const ref = seatsRef(storeId);
        await get(ref);
        await runTransaction(ref, (current) => decideRelease(current, me) ?? undefined, {
          applyLocally: false,
        });
      })(),
      timeoutMs
    );
  } catch (error) {
    console.warn("[seat] คืนที่นั่งไม่สำเร็จ", error);
  }
}

/** ชื่อร้านไว้โชว์ในหน้า static — อ่านไม่ได้ / ช้า ก็คืน "" (ไม่ขวางการพาไปหน้า static) */
export async function readStoreName(storeId: string, timeoutMs = 2000): Promise<string> {
  try {
    const snapshot = await withTimeout(get(ref(getDatabase(), `stores/${storeId}/_name`)), timeoutMs);
    const name = snapshot.val();
    return typeof name === "string" ? name : "";
  } catch {
    return "";
  }
}

/** ต่ออายุ lastSeen ของที่นั่งตัวเอง */
export async function touchSeat(storeId: string, mode: SeatMode, me: string): Promise<void> {
  const base = mode === SEAT_MODE_MAIN ? "main" : `second/${me}`;
  try {
    await update(seatsRef(storeId), {
      [`${base}/lastSeen`]: serverTimestamp(),
      // serverTimestamp() แปลงเป็นข้อความไม่ได้ ใช้เวลา server ที่เครื่องคำนวณไว้แทน
      [`${base}/lastSeenIso`]: toLocalIso(serverNow()),
    });
  } catch (error) {
    console.warn("[seat] heartbeat ไม่สำเร็จ", error);
  }
}

export function watchSeats(storeId: string, onChange: (value: any) => void): Unsubscribe {
  return onValue(
    seatsRef(storeId),
    (snapshot) => onChange(snapshot.val()),
    (error) => console.warn("[seat] อ่านที่นั่งไม่ได้", error)
  );
}

export function waitForConnected(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    let unsub: Unsubscribe | null = null;
    const finish = (connected: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (unsub) unsub();
      resolve(connected);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    unsub = onValue(ref(getDatabase(), ".info/connected"), (snapshot) => {
      if (snapshot.val() === true) finish(true);
    });
    // callback อาจถูกเรียกทันทีก่อนได้ unsub กลับมา
    if (done) unsub();
  });
}

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}
